import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { sanitizeHtml } from "../lib/sanitize";
import { api } from "../lib/api";
import { DocsTOC } from "../components/docs/DocsTOC";
import { DocsLoader } from "../components/docs/DocsLoader";
import { ArrowLeft } from "lucide-react";
import { useReadThemeCtx } from "../lib/ReadThemeContext";

// Only the "new" status is shown publicly. Rewritten/refreshed are
// admin-only signals (visible in /admin/docs).
const PUBLIC_STATUS_LABELS = {
  new: "New",
};

export default function DocsPage() {
  const { sectionSlug, pageSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { theme } = useReadThemeCtx();
  const isLight = theme === "light";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setPage(null);
    // Scroll-reset on navigation is handled at the DocsLayout level via
    // useLayoutEffect (fires before paint, so the previous scroll position
    // never bleeds through).
    (async () => {
      try {
        const r = await api.get(`/docs/${sectionSlug}/${pageSlug}`);
        if (!cancelled) setPage(r.data);
      } catch (e) {
        if (e?.response?.status !== 404) {
          if (!cancelled) setError("Failed to load page");
          return;
        }
        // 404 → try docs redirect map
        try {
          const rr = await api.get("/docs/redirects");
          const probe = `/${sectionSlug}/${pageSlug}`;
          const hit = (rr.data.redirects || []).find((x) => x.from_path === probe);
          if (hit?.to_path) {
            navigate(`/docs${hit.to_path}`, { replace: true });
            return;
          }
        } catch (err) {
          // Non-fatal: redirect lookup failed (network or 5xx). We still
          // show "Page not found" below.
          console.warn("[docs] redirect lookup failed:", err);
        }
        if (!cancelled) setError("Page not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sectionSlug, pageSlug, navigate]);

  // After content renders, scroll to URL hash if any
  useEffect(() => {
    if (!page) return;
    const hash = location.hash?.slice(1);
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: "instant" in window ? "instant" : "auto" });
    }
  }, [page, location.hash]);

  if (loading) {
    return <DocsLoader isLight={isLight} />;
  }

  if (error || !page) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center" data-testid="docs-page-not-found">
        <p className={`font-serif text-2xl ${isLight ? "text-aubergine" : "text-bone"}`}>{error || "Page not found"}</p>
        <Link to="/docs" className={`mt-4 inline-flex items-center font-mono text-xs uppercase tracking-wider ${isLight ? "text-champagne hover:text-aubergine" : "text-champagne hover:text-champ-light"}`}>
          <ArrowLeft className="mr-2 h-3.5 w-3.5" /> Back to docs
        </Link>
      </div>
    );
  }

  const publicStatusLabel = page.status && PUBLIC_STATUS_LABELS[page.status];
  const breadcrumbLink = isLight ? "text-aubergine/55 hover:text-aubergine" : "text-bone/55 hover:text-bone";
  const breadcrumbSep = isLight ? "text-aubergine/30" : "text-bone/30";
  const breadcrumbCurrent = isLight ? "text-aubergine/55" : "text-bone/55";
  const titleColor = isLight ? "text-aubergine" : "text-bone";
  const descColor = isLight ? "text-aubergine/70" : "text-bone/70";
  const ruleBorder = isLight ? "border-aubergine/10" : "border-champ-light/10";

  return (
    <article data-testid="docs-page">
      <Helmet>
        <title>{`${page.title} · T-REX Network Docs`}</title>
        <meta name="description" content={page.description || ""} />
        <meta property="og:title" content={`${page.title} · T-REX Network Docs`} />
        <meta property="og:description" content={page.description || ""} />
        <meta property="og:type" content="article" />
      </Helmet>
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
        <div className="lg:col-span-9">
          {/* Breadcrumb + status */}
          <div className="mb-4 flex items-center gap-3 flex-wrap" data-testid="docs-breadcrumb">
            <Link to="/docs" className={`font-mono text-[11px] uppercase tracking-[0.15em] ${breadcrumbLink}`}>
              Docs
            </Link>
            <span className={`font-mono text-[11px] ${breadcrumbSep}`}>/</span>
            <span className={`font-mono text-[11px] uppercase tracking-[0.15em] ${breadcrumbCurrent}`}>
              {page.section_slug.replace(/-/g, " ")}
            </span>
            {publicStatusLabel && (
              <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-champagne/15 text-champagne px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider" data-testid={`docs-page-status-${page.status}`}>
                <span className="h-1.5 w-1.5 rounded-full bg-champagne" />
                {publicStatusLabel}
              </span>
            )}
          </div>
          <h1 className={`trex-h1 leading-[1.1] ${titleColor}`}>{page.title}</h1>
          {page.description && (
            <p className={`mt-4 text-lg leading-relaxed ${descColor}`}>{page.description}</p>
          )}

          <div className={`mt-10 border-t pt-6 ${ruleBorder}`}>
            <div
              className="prose-trex"
              data-testid="docs-page-content"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.content_html) }}
            />
          </div>
        </div>
        <div className="hidden lg:block lg:col-span-3">
          <DocsTOC toc={page.toc || []} />
        </div>
      </div>
    </article>
  );
}
